import { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseISO, format } from 'date-fns';

import Loading from "../../../components/Loading";
import Data from '../../../../repository';
import { updateDiscountCodeSchema } from '../../../../validation/schemas';

import styles from '../../../../styles/Admin.module.scss';

function UpdateDiscountCodeForm() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [serverError, setServerError] = useState(null);
	const [discountCode, setDiscountCode] = useState(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		control
	} = useForm({
		resolver: yupResolver(updateDiscountCodeSchema),
		defaultValues: {
			name: '',
			start: '',
			end: '',
			enabled: true
		}
	});

	useEffect(() => {
		fetchDiscountCode();
	}, []);

	const fetchDiscountCode = async () => {
		setLoading(true);
		try {
			const result = await Data.fetchDiscountCode(id);
			const data = result.data;
			setDiscountCode(data);
			reset({
				name: data.name || '',
				start: moment(data.start).format("YYYY-MM-DD"),
				end: moment(data.end).format("YYYY-MM-DD"),
				enabled: data.enabled
			});
		} catch (error) {
			setServerError('Failed to load discount code');
		} finally {
			setLoading(false);
		}
	}

	const onSubmit = async (data) => {
		setSaving(true);
		setServerError(null);

		try {
			await Data.updateDiscountCode({
				...discountCode,
				name: data.name,
				start: data.start,
				end: data.end,
				enabled: data.enabled === 'true' || data.enabled === true
			});
			navigate('/admin/discount-codes');
		} catch (error) {
			setServerError(error.response?.data?.message || 'Failed to update discount code. Please try again.');
			setSaving(false);
		}
	}

	if (loading) return <Loading button={false} />;

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
			<div>
				<p>Name: *</p>
				<input
					type="text"
					{...register('name')}
					className={errors.name ? 'input-error' : ''}
				/>
				{errors.name && <span className="form-field-error">{errors.name.message}</span>}
			</div>

			<div>
				<p>Code:</p>
				<input type="text" value={discountCode?.code || ''} disabled={true} />
			</div>

			<div>
				<p>Discount Type:</p>
				<input type="text" value={discountCode?.discountType === 'amount' ? 'Amount Off' : 'Percentage Off'} disabled={true} />
			</div>

			{discountCode?.discountType === 'amount' ? (
				<div>
					<p>Amount Off:</p>
					<input type="text" value={`£${((discountCode?.amountOff || 0) / 100).toFixed(2)}`} disabled={true} />
				</div>
			) : (
				<div>
					<p>Percentage Off:</p>
					<input type="number" value={discountCode?.percentageOff || 0} disabled={true} />
				</div>
			)}

			<div>
				<p>Valid From: *</p>
				<Controller
					name="start"
					control={control}
					render={({ field }) => (
						<DatePicker
							value={field.value ? parseISO(field.value) : null}
							onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
							slotProps={{ textField: { className: errors.start ? 'input-error' : '' } }}
						/>
					)}
				/>
				{errors.start && <span className="form-field-error">{errors.start.message}</span>}
			</div>

			<div>
				<p>Valid Until: *</p>
				<Controller
					name="end"
					control={control}
					render={({ field }) => (
						<DatePicker
							value={field.value ? parseISO(field.value) : null}
							onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
							slotProps={{ textField: { className: errors.end ? 'input-error' : '' } }}
						/>
					)}
				/>
				{errors.end && <span className="form-field-error">{errors.end.message}</span>}
			</div>

			<div>
				<p>Status:</p>
				<select {...register('enabled')}>
					<option value="true">Enabled</option>
					<option value="false">Disabled</option>
				</select>
			</div>

			{serverError && <p style={{ color: 'red' }}>{serverError}</p>}

			<button type="submit" className="btn btnPrimary" disabled={saving}>
				{saving ?
					<Loading button={true} />
					:
					`Save Changes`
				}
			</button>
		</form>
	)
}

export default UpdateDiscountCodeForm;
